import React, { useEffect, useState } from "react";
import {
  Card,
  FormControlLabel,
  Grid,
  Icon,
  InputAdornment,
  Radio,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Select,
  FormControl,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import { STATUS } from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
} from "components/CommonComponent/CommonFunction";
import { ArrowDropDown } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  getUnitListing,
  editUnit,
  addUnit,
  deleteUnit,
  getUnitDetailsById,
} from "actions/unit";
import { unitList } from "constants/unit";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import {
  DEFAULT_RECORDS_PER_PAGE,
  hasAccess,
} from "components/common/constant";

const initialFields = {
  id: null,
  number: {
    value: "",
    error: "",
    isChange: false,
  },

  status: {
    value: "ACTIVE",
    error: "",
    isChange: false,
  },
};
function UnitListing() {
  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("UNIT_MASTER", "CREATE")?.includes(currentRole);
  const canUpdate = hasAccess("UNIT_MASTER", "UPDATE")?.includes(currentRole);
  const canDelete = hasAccess("UNIT_MASTER", "DELETE")?.includes(currentRole);

  const canShowActions = canUpdate || canDelete;
  const dispatch = useDispatch();
  const unitListingData = useSelector((state) => state.unit.unitList);
  const { notify } = useNotify();
  const [panelMode, setPanelMode] = useState(null); //add /edit
  const [unitFormData, setUnitFormData] = useState(initialFields);
  const [searchState, setSearchState] = useState({
    number: "",
    status: null,
    filterParams: [],
  });
  const [pageNum, setPageNum] = useState(1);
  const [unitListState, setUnitListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  //api to fetch unit list
  useEffect(() => {
    getUnitListing(dispatch, pageNum, searchState.filterParams);
    return () => {
      dispatch({ type: unitList, payload: {} });
    }; //unmount the redux data
  }, [dispatch, pageNum, searchState.filterParams]);

  //check the listing data state using code and message
  useEffect(() => {
    if (unitListingData?.code === 200) {
      setUnitListState({
        isLoading: false,
        error: "",
        noRecords: unitListingData?.data?.Unit?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (unitListingData?.code || unitListingData?.message) {
      setUnitListState({
        isLoading: false,
        error:
          unitListingData?.message ||
          "Failed to load unit list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [unitListingData]);

  const formattedListData = unitListingData?.data?.Unit?.map((item, index) => ({
    id: item.ID,
    number: item.unitNo,
    status: item.status,
    srNo:
      (pageNum - 1) *
        (unitListingData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
  }));

  const handleChange = (field, value) => {
    setUnitFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChange: true,
      },
    }));
  };

  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([, field]) => field?.isChange)
        .map(([key, field]) => {
          if (key === "number") return ["unitNo", field.value];
          if (key === "status") return ["status", field.value];
          return null;
        })
        .filter(Boolean),
    );
  //add and edit function
  const handleSubmit = async () => {
    if (!validateForm()) return;
    const isEdit = panelMode === "edit";
    let bodyData = {
      unitNo: unitFormData.number.value,
      status: unitFormData.status.value,
    };

    if (isEdit) {
      const editedData = getEditedBodyData(unitFormData);
      if (Object.keys(editedData).length === 0) {
        notify("No changes detected", "info");
        return;
      }
      bodyData = editedData;
    }
    try {
      setUnitListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const res = isEdit
        ? await editUnit(unitFormData.id, bodyData)
        : await addUnit(bodyData);
      if (res?.code === 200) {
        notify(
          res?.message || `Unit ${isEdit ? "updated" : "added"} successfully`,
          "success",
        );
        closeModal();
        // pageNum === 1
           getUnitListing(dispatch, pageNum, searchState.filterParams)
          // : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save unit";

        notify(message, "error");
      }
    } catch (error) {
      notify(`Failed to ${isEdit ? "update" : "add"} unit`, "error");
    } finally {
      setUnitListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setUnitFormData({
      ...initialFields,
    });
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "number":
        return !trimmedValue ? "Please enter unit number" : "";

      default:
        return "";
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setUnitFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...unitFormData };

    Object.keys(newData).forEach((key) => {
      if (key === "id") return;
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });

    setUnitFormData(newData);
    return Object.values(newData)
      .filter((item, index) => Object.keys(newData)[index] !== "id")
      .every((item) => item.error === "");
  };

  const openAddModal = () => {
    // e.stopPropagation();
    setPanelMode("add");
    setUnitFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setUnitListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const res = await getUnitDetailsById(row.id);
      const unit = res.data;

      if (res?.code === 200) {
        setUnitFormData({
          id: unit?.ID,
          number: {
            value: unit?.unitNo || "",
            error: "",
            isChange: false,
          },
          status: {
            value: res?.data?.status || "ACTIVE",
            error: "",
            isChange: false,
          },
        });
      } else {
        notify(res?.message || "Failed to fetch unit details", "error");
        closeModal();
      }
    } catch (error) {
      notify("Something went wrong while fetching unit details", "error");
      closeModal();
    } finally {
      setUnitListState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleDelete = (row) => {
    showAlert({
      title: `Are you sure you want to delete this unit?`,
      message: `Unit "${row.number}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          setUnitListState((prev) => ({
            ...prev,
            isLoading: true,
          }));
          const res = await deleteUnit(row.id);

          if (res?.code === 200) {
            notify(res?.message || "Unit deleted successfully", "success");
            pageNum === 1
              ? getUnitListing(dispatch, pageNum, searchState.filterParams)
              : setPageNum(1);
          } else {
            notify(res?.message || "Failed to delete unit", "error");
          }
        } catch (error) {
          notify("Something went wrong while deleting unit", "error");
        } finally {
          setUnitListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },
      onCancel: () => {},
    });
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    return (
      <MDBadge
        badgeContent={status}
        color={status.toUpperCase() === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const columns = [
    {
      Header: "Sr No.",
      accessor: "srNo",
      align: "left",
    },
    {
      Header: "Unit No",
      accessor: "number",
      align: "left",
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption">|</MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];
  // filters for search
  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.number) {
      filterParams.push({
        field: "UnitNo",
        condition: "like",
        filterValues: [searchState.number],
      });
    }

    if (searchState.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setUnitListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setSearchState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setSearchState({
      number: "",
      status: null,

      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Units
                </MDTypography>

                {canCreate && (
                  <MDButton
                    type="button"
                    variant="contained"
                    color="deepBlue"
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      textTransform: "none",
                    }}
                    onClick={openAddModal}
                  >
                    Add Unit
                  </MDButton>
                )}
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      placeholder="Search by number"
                      size="small"
                      value={searchState.number}
                      onChange={(e) =>
                        setSearchState({
                          ...searchState,
                          number: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={searchState.status || ""}
                        displayEmpty
                        onChange={(e) =>
                          setSearchState({
                            ...searchState,
                            status: e.target.value,
                          })
                        }
                        IconComponent={ArrowDropDown}
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                 Select Status
                              </span>
                            );

                          return renderStatusBadge(selected);
                        }}
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={unitListState.showLoaderOnClick}
                      >
                        {unitListState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {searchState.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox
                px={3}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {unitListingData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {unitListState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : unitListState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography color="error" variant="body2">
                      {unitListState.error}
                    </MDTypography>
                  </MDBox>
                ) : unitListState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="20vh"
                  >
                    <MDTypography color="text" variant="body2">
                      No units found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    data={formattedListData || []}
                    columns={columns}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (unitListingData?.data?.totalRecords || 0) /
                        (unitListingData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              {panelMode === "edit" ? "Edit Unit" : "Add Unit"}
            </MDTypography>

            {unitListState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Number</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="number"
                    placeholder="Enter unit number"
                    value={unitFormData.number.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!unitFormData.number.error}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />
                  {unitFormData.number.error && (
                    <MDTypography variant="caption" color="error">
                      {unitFormData.number.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={unitFormData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={unitListState.showLoaderOnClick}
                    >
                      {unitListState.showLoaderOnClick ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        </>
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default UnitListing;
